import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  DataTable,
  type DataTableColumn,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
  type SortDirection,
} from '../components/ListControls';
import { toast } from 'sonner';
import { CLIENT_FINDERS_PAYOUTS, CLIENT_HISTORY } from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type HistoryStatus = 'claimed' | 'pending';
type PayoutStatus = 'paid' | 'pending';
type PayoutStatusFilter = 'all' | PayoutStatus;
type PayoutSortKey = 'date' | 'accountId' | 'fee' | 'status' | 'received' | 'total';
type HistorySortKey = 'date' | 'accountId' | 'token' | 'qty' | 'debtPaid' | 'profitLoss' | 'status';

interface HistoryEvent {
  id: string;
  date: string;
  accountId: string;
  token: string;
  tokenColor: string;
  qty: number;
  debtPaid: number;
  profitLoss: number;
  profitPercent: number;
  status: HistoryStatus;
  txHash: string;
}

interface FindersPayout {
  id: string;
  accountId: string;
  date: string;
  fee: number;
  status: PayoutStatus;
  received: number;
  total: number;
  txHash: string;
}

const HISTORY: HistoryEvent[] = CLIENT_HISTORY;
const FINDERS_PAYOUTS: FindersPayout[] = CLIENT_FINDERS_PAYOUTS;

const ALL_TOKENS = [...new Set(HISTORY.map((h) => h.token))].sort();

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmt(value: number): string {
  return `$${Math.abs(value).toLocaleString()}`;
}

function parseDateToTimestamp(value: string): number {
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) return parsed;
  const [day, month, year] = value.split('/').map((part) => Number(part));
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b);
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export function LiquidationHistoryPage() {
  const navigate = useNavigate();
  const [payoutSearch, setPayoutSearch] = useState('');
  const [payoutStatusFilter, setPayoutStatusFilter] = useState<PayoutStatusFilter>('all');
  const [payoutSortKey, setPayoutSortKey] = useState<PayoutSortKey>('date');
  const [payoutSortDirection, setPayoutSortDirection] = useState<SortDirection>('desc');
  const [payoutPage, setPayoutPage] = useState(1);
  const [payoutPageSize, setPayoutPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  const [historySearch, setHistorySearch] = useState('');
  const [tokenFilter, setTokenFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [historySortKey, setHistorySortKey] = useState<HistorySortKey>('date');
  const [historySortDirection, setHistorySortDirection] = useState<SortDirection>('desc');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  const filteredPayouts = useMemo(() => {
    let result = [...FINDERS_PAYOUTS];
    if (payoutStatusFilter !== 'all') result = result.filter((p) => p.status === payoutStatusFilter);
    if (payoutSearch.trim()) {
      const q = payoutSearch.toLowerCase();
      result = result.filter(
        (p) => p.accountId.toLowerCase().includes(q) || p.txHash.toLowerCase().includes(q),
      );
    }
    return result;
  }, [payoutSearch, payoutStatusFilter]);

  const sortedPayouts = useMemo(() => {
    const result = [...filteredPayouts];
    result.sort((a, b) => {
      let comparison = 0;
      switch (payoutSortKey) {
        case 'date':
          comparison = parseDateToTimestamp(a.date) - parseDateToTimestamp(b.date);
          break;
        case 'accountId':
          comparison = compareText(a.accountId, b.accountId);
          break;
        case 'fee':
          comparison = a.fee - b.fee;
          break;
        case 'status':
          comparison = compareText(a.status, b.status);
          break;
        case 'received':
          comparison = a.received - b.received;
          break;
        case 'total':
          comparison = a.total - b.total;
          break;
      }
      return payoutSortDirection === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [filteredPayouts, payoutSortDirection, payoutSortKey]);

  const paginatedPayouts = useMemo(() => {
    const start = (payoutPage - 1) * payoutPageSize;
    return sortedPayouts.slice(start, start + payoutPageSize);
  }, [payoutPage, payoutPageSize, sortedPayouts]);

  const filteredHistory = useMemo(() => {
    let result = [...HISTORY];
    if (tokenFilter !== 'all') result = result.filter((e) => e.token === tokenFilter);
    if (statusFilter !== 'all') result = result.filter((e) => e.status === statusFilter);
    if (historySearch.trim()) {
      const q = historySearch.toLowerCase();
      result = result.filter(
        (e) =>
          e.accountId.toLowerCase().includes(q)
          || e.token.toLowerCase().includes(q)
          || e.txHash.toLowerCase().includes(q),
      );
    }
    return result;
  }, [historySearch, tokenFilter, statusFilter]);

  const sortedHistory = useMemo(() => {
    const result = [...filteredHistory];
    result.sort((a, b) => {
      let comparison = 0;
      switch (historySortKey) {
        case 'date':
          comparison = parseDateToTimestamp(a.date) - parseDateToTimestamp(b.date);
          break;
        case 'accountId':
          comparison = compareText(a.accountId, b.accountId);
          break;
        case 'token':
          comparison = compareText(a.token, b.token);
          break;
        case 'qty':
          comparison = a.qty - b.qty;
          break;
        case 'debtPaid':
          comparison = a.debtPaid - b.debtPaid;
          break;
        case 'profitLoss':
          comparison = a.profitLoss - b.profitLoss;
          break;
        case 'status':
          comparison = compareText(a.status, b.status);
          break;
      }
      return historySortDirection === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [filteredHistory, historySortDirection, historySortKey]);

  const paginatedHistory = useMemo(() => {
    const start = (historyPage - 1) * historyPageSize;
    return sortedHistory.slice(start, start + historyPageSize);
  }, [historyPage, historyPageSize, sortedHistory]);

  const totalLiquidations = HISTORY.length;
  const totalDebtRepaid = HISTORY.reduce((s, e) => s + e.debtPaid, 0);
  const totalCollateral = HISTORY.reduce((s, e) => s + e.qty, 0);
  const netProfitLoss = HISTORY.reduce((s, e) => s + e.profitLoss, 0);

  const totalEarned = FINDERS_PAYOUTS.reduce((s, p) => s + p.fee, 0);
  const paidOut = FINDERS_PAYOUTS.filter((p) => p.status === 'paid').reduce((s, p) => s + p.received, 0);
  const pendingAmount = totalEarned - paidOut;

  function onSortPayouts(nextKey: PayoutSortKey) {
    setPayoutSortDirection((prev) => (
      payoutSortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    ));
    setPayoutSortKey(nextKey);
    setPayoutPage(1);
  }

  function onSortHistory(nextKey: HistorySortKey) {
    setHistorySortDirection((prev) => (
      historySortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    ));
    setHistorySortKey(nextKey);
    setHistoryPage(1);
  }

  const payoutColumns = useMemo<DataTableColumn<FindersPayout, PayoutSortKey>[]>(
    () => [
      {
        id: 'date',
        label: 'Date',
        sortable: true,
        sortKey: 'date',
        renderCell: (payout) => <span className="text-xs text-muted-foreground">{payout.date}</span>,
      },
      {
        id: 'accountId',
        label: 'Account',
        sortable: true,
        sortKey: 'accountId',
        renderCell: (payout) => <span className="font-mono text-xs">{payout.accountId}</span>,
      },
      {
        id: 'fee',
        label: 'Fee',
        align: 'right',
        sortable: true,
        sortKey: 'fee',
        renderCell: (payout) => `$${payout.fee}`,
      },
      {
        id: 'status',
        label: 'Status',
        align: 'center',
        sortable: true,
        sortKey: 'status',
        renderCell: (payout) => (
          <div className="flex justify-center">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              payout.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {payout.status === 'paid' ? 'Paid' : 'Pending'}
            </span>
          </div>
        ),
      },
      {
        id: 'received',
        label: 'Received',
        align: 'right',
        sortable: true,
        sortKey: 'received',
        renderCell: (payout) => `$${payout.received}`,
      },
      {
        id: 'total',
        label: 'Total',
        align: 'right',
        sortable: true,
        sortKey: 'total',
        renderCell: (payout) => `$${payout.total}`,
      },
      {
        id: 'tx',
        label: 'Tx',
        align: 'center',
        renderCell: (payout) => (
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1 text-xs font-mono text-primary">
              {payout.txHash} <Icon name="streamline:share-link" className="h-3 w-3" />
            </span>
          </div>
        ),
      },
    ],
    [],
  );

  const historyColumns = useMemo<DataTableColumn<HistoryEvent, HistorySortKey>[]>(
    () => [
      {
        id: 'date',
        label: 'Date',
        sortable: true,
        sortKey: 'date',
        renderCell: (event) => <span className="text-xs text-muted-foreground">{event.date}</span>,
      },
      {
        id: 'accountId',
        label: 'Account',
        sortable: true,
        sortKey: 'accountId',
        renderCell: (event) => <span className="font-mono text-xs">{event.accountId}</span>,
      },
      {
        id: 'token',
        label: 'Collateral',
        sortable: true,
        sortKey: 'token',
        renderCell: (event) => (
          <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-semibold ${event.tokenColor}`}>
            {event.token}
          </span>
        ),
      },
      {
        id: 'qty',
        label: 'Qty',
        align: 'right',
        sortable: true,
        sortKey: 'qty',
        renderCell: (event) => <span className="text-xs">{event.qty}</span>,
      },
      {
        id: 'debtPaid',
        label: 'Debt Paid',
        align: 'right',
        sortable: true,
        sortKey: 'debtPaid',
        renderCell: (event) => fmt(event.debtPaid),
      },
      {
        id: 'profitLoss',
        label: 'Profit/Loss',
        align: 'right',
        sortable: true,
        sortKey: 'profitLoss',
        renderCell: (event) => (
          <span className={`font-medium ${event.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {event.profitLoss >= 0 ? '+' : '-'}
            {fmt(event.profitLoss)} ({event.profitPercent.toFixed(1)}%)
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        align: 'center',
        sortable: true,
        sortKey: 'status',
        renderCell: (event) => (
          <div className="flex justify-center">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              event.status === 'claimed' ? 'bg-green-100 text-green-700' : 'bg-gray-800 text-gray-200'
            }`}>
              {event.status === 'claimed' ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3 w-3" /> : <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3 w-3" />}
              {event.status}
            </span>
          </div>
        ),
      },
      {
        id: 'tx',
        label: 'Tx',
        align: 'center',
        renderCell: () => (
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline">
              View <Icon name="streamline:share-link" className="h-3 w-3" />
            </span>
          </div>
        ),
      },
    ],
    [],
  );

  function handleExportCsv() {
    const headers = 'Date,Account,Token,Qty,Debt Paid,Profit/Loss,Status,Tx Hash\n';
    const rows = HISTORY.map((e) =>
      `${e.date},${e.accountId},${e.token},${e.qty},${e.debtPaid},${e.profitLoss},${e.status},${e.txHash}`,
    ).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liquidation-history-${Date.now()}.csv`;
    a.click();
    toast.success('CSV exported');
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Liquidation History</h1>
            <p className="text-sm text-muted-foreground">
              Track your liquidation performance and past transactions.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv}>
            <Icon name="streamline:download-file" className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => navigate('/liquidations')}>Find Opportunities</Button>
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard title="Total Liquidations" value={String(totalLiquidations)} subtitle="All time" icon={<Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Total Debt Repaid" value={fmt(totalDebtRepaid)} subtitle="USDC repaid" icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Collateral Acquired" value={totalCollateral.toFixed(1)} subtitle="Total tokens" icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard
          title="Net Profit/Loss"
          value={`${netProfitLoss >= 0 ? '+' : '-'}${fmt(netProfitLoss)}`}
          subtitle="Total earnings"
          icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-muted-foreground" />}
          valueClassName={netProfitLoss >= 0 ? 'text-green-600' : 'text-red-600'}
        />
      </div>

      {/* Finder's Fee Payouts */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle className="flex items-center gap-2">
            <Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5" />
            Finder&apos;s Fee Payouts
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {/* Summary row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 mb-6">
            <div className="rounded-lg border border-border p-4">
              <span className="text-xs text-muted-foreground">Total Earned</span>
              <div className="text-xl font-bold mt-1">${totalEarned}</div>
              <span className="text-xs text-muted-foreground">All liquidations</span>
            </div>
            <div className="rounded-lg border border-border p-4">
              <span className="text-xs text-muted-foreground">Paid Out</span>
              <div className="text-xl font-bold text-green-600 mt-1">${paidOut}</div>
              <span className="text-xs text-muted-foreground">Received</span>
            </div>
            <div className="rounded-lg border border-border p-4">
              <span className="text-xs text-muted-foreground">Pending</span>
              <div className="text-xl font-bold text-amber-600 mt-1">${pendingAmount}</div>
              <span className="text-xs text-muted-foreground">In process</span>
            </div>
          </div>

          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Filter by status</label>
              <select
                value={payoutStatusFilter}
                onChange={(e) => { setPayoutStatusFilter(e.target.value as PayoutStatusFilter); setPayoutPage(1); }}
                className={`${INPUT_CLASS} w-full sm:w-44`}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <SearchBar
              value={payoutSearch}
              onChange={(v) => { setPayoutSearch(v); setPayoutPage(1); }}
              placeholder="Search account or tx hash..."
              className="w-full sm:w-72"
            />
          </div>

          <div className="hidden md:block">
            <DataTable<FindersPayout, PayoutSortKey>
              columns={payoutColumns}
              rows={paginatedPayouts}
              rowKey={(payout) => payout.id}
              sort={{
                key: payoutSortKey,
                direction: payoutSortDirection,
                onSort: onSortPayouts,
              }}
              emptyMessage="No payouts match your filters."
            />
          </div>

          <div className="space-y-3 md:hidden">
            {paginatedPayouts.map((payout) => (
              <div key={payout.id} className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">{payout.accountId}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    payout.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {payout.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">{payout.date}</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Fee</span>
                    <div className="font-medium">${payout.fee}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Received</span>
                    <div className="font-medium">${payout.received}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">Total</span>
                    <div className="font-medium">${payout.total}</div>
                  </div>
                </div>
                <div className="text-xs font-mono text-primary inline-flex items-center gap-1">
                  {payout.txHash} <Icon name="streamline:share-link" className="h-3 w-3" />
                </div>
              </div>
            ))}
          </div>

          {filteredPayouts.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No payouts match your filters.
            </div>
          )}

          <PaginationControls
            page={payoutPage}
            setPage={setPayoutPage}
            pageSize={payoutPageSize}
            setPageSize={setPayoutPageSize}
            totalItems={filteredPayouts.length}
            label="payouts"
          />
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Filter by token</label>
                <select
                  value={tokenFilter}
                  onChange={(e) => { setTokenFilter(e.target.value); setHistoryPage(1); }}
                  className={`${INPUT_CLASS} w-full sm:w-40`}
                >
                  <option value="all">All Tokens</option>
                  {ALL_TOKENS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Filter by status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setHistoryPage(1); }}
                  className={`${INPUT_CLASS} w-full sm:w-40`}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="claimed">Claimed</option>
                </select>
              </div>
            </div>
            <SearchBar
              value={historySearch}
              onChange={(v) => { setHistorySearch(v); setHistoryPage(1); }}
              placeholder="Search account, token or tx hash..."
              className="w-full sm:w-72"
            />
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <DataTable<HistoryEvent, HistorySortKey>
              columns={historyColumns}
              rows={paginatedHistory}
              rowKey={(event) => event.id}
              sort={{
                key: historySortKey,
                direction: historySortDirection,
                onSort: onSortHistory,
              }}
              emptyMessage="No transactions match your filters."
            />
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-3">
            {paginatedHistory.map((event) => (
              <div key={event.id} className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${event.tokenColor}`}>
                      {event.token}
                    </span>
                    <span className="font-mono text-xs">{event.accountId}</span>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    event.status === 'claimed' ? 'bg-green-100 text-green-700' : 'bg-gray-800 text-gray-200'
                  }`}>
                    {event.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">{event.date}</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Qty</span>
                    <div className="font-medium">{event.qty}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Debt Paid</span>
                    <div className="font-medium">{fmt(event.debtPaid)}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">P/L</span>
                    <div className={`font-medium ${event.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {event.profitLoss >= 0 ? '+' : '-'}{fmt(event.profitLoss)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredHistory.length === 0 && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No transactions match your filters.
            </div>
          )}

          <PaginationControls
            page={historyPage}
            setPage={setHistoryPage}
            pageSize={historyPageSize}
            setPageSize={setHistoryPageSize}
            totalItems={filteredHistory.length}
            label="transactions"
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
  valueClassName,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className={`mt-2 text-2xl sm:text-3xl font-bold ${valueClassName ?? ''}`}>{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
